<template>
    <c-modal :value="true" :title="`Add member`" @closed="cancelHandler">
        <validation-observer ref="createFormRef">
            <div class="form-group">
                <validation-provider v-slot="{ failed }" rules="required">
                    <c-input
                        v-model="memberModel.name"
                        label="Name"
                        label-class="text-muted small2"
                        class="mb-2"
                        required
                        :error="failed"
                        error-msg="Enter the name of the user"
                    />
                </validation-provider>

                <validation-provider v-slot="{ failed }" rules="required">
                    <c-input
                        v-model="memberModel.email"
                        label="Email"
                        label-class="text-muted small2"
                        class="mb-2"
                        required
                        :error="failed"
                        error-msg="Enter the email of the user"
                    />
                </validation-provider>

                <validation-provider v-slot="{ failed }" rules="required">
                    <div class="mb-1">
                        <span class="text-muted small">Access level*</span>
                    </div>
                    <c-button-group
                        v-model="memberModel.accessType"
                        no-icon
                        type="radio"
                        :items="accessTypes"
                        :error="failed"
                        error-msg="Select the access mode"
                    />
                    <small class="mt-2 text-muted">
                        With restricted access, a member can only see allowed templates and the ones they create.
                    </small>
                    <c-custom-checkbox
                        v-if="memberModel.accessType == AccessMode.RESTRICTED"
                        v-model="memberModel.manageIntegrations"
                        label="Access Account Integrations"
                        class="mt-3"
                        switch
                    />
                </validation-provider>
            </div>
        </validation-observer>

        <template #modal-footer>
            <div class="d-flex justify-content-between w-100">
                <c-button text="Cancel" variant="secondary" @click="cancelHandler" />
                <c-button text="Add" variant="primary" @click="execLoadedFn(createMember)" />
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { Ref, reactive, ref } from 'vue';
import { useLoader } from '@/composables/loader';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import CModal from '@/components/CModal.vue';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton';
import CCustomCheckbox from '@/components/shared/CCustomCheckbox.vue';
import CButtonGroup, { CButtonGroupItem } from '@/components/shared/CButtonGroup';
import { addTeamMember } from '@/api/team';
import { AccessMode, TeamMember } from '@/api/typings/team';

const emit = defineEmits<{
    (event: 'member-created', value: TeamMember): void;
    (event: 'handle-permissions', value: TeamMember): void;
    (event: 'cancel'): void;
}>();

const accessTypes: CButtonGroupItem[] = [
    {
        label: 'Full access',
        value: AccessMode.FULL
    },
    {
        label: 'Restricted',
        value: AccessMode.RESTRICTED
    }
];

const { execLoadedFn } = useLoader();

const createFormRef: Ref<InstanceType<typeof ValidationObserver> | null> = ref(null);

const memberModel = reactive({
    name: '',
    email: '',
    accessType: AccessMode.FULL,
    manageIntegrations: false
});

const createMember = async (): Promise<void> => {
    try {
        const newMember = await addTeamMember(
            memberModel.name,
            memberModel.email,
            memberModel.accessType,
            memberModel.manageIntegrations
        );

        emit('member-created', newMember);
        if (memberModel.accessType == AccessMode.RESTRICTED) emit('handle-permissions', newMember);
    } catch (error) {
        console.error('Failed to invite user', error);
    }
};

const cancelHandler = () => emit('cancel');
</script>
<style lang="scss">
.small2 {
    font-size: 0.875em !important;
    font-weight: 400 !important;
}
</style>
